import './umuVideoSelector.scss';

import * as React from 'react';
const { useEffect, useState, useRef } = React;
import { lang } from '@umu/ukit-lang';
import { If } from 'common/src/components/if';
import { Loading } from 'common/src/components';
import { defaultPageInfo } from 'common/src/config';
import { Modal, Iconfont, Tooltip, Button } from '@umu/umu-design';
import { ListBlankPC, Pagination } from 'businessCommon/src/components';
import { getResourceArr, IMediaData } from '@umu/umu-api/src/services/resource/getResourceArr';
import ImageImageHandle from '@umu/umu-image-handle';

interface IUmuVideoSelectorProps {
    visible?: boolean;
    allowUpload?: boolean;
    status_str?: string;
    cancel: VoidFunction;
    done(file: IMediaData): void;
}

const formatFileSize = function (text: string) {
    var length = parseInt(text, 10);
    if (length < 1024) {
        return length + 'B';
    }
    var size = ['B', 'KB', 'MB', 'GB', 'TP', 'PB', 'EB'];
    var prettyText = 0;

    var sizeCount = 0;
    var sizeCountLength = size.length;
    while (length > 1 && sizeCount < sizeCountLength) {
        sizeCount = sizeCount + 1;
        prettyText = Math.round(length * 100) / 100;
        length = length / 1024;
    }

    return parseFloat(prettyText.toFixed(1)) + size[sizeCount - 1];
};

const uplinkText = lang(
    `暂不支持上传视频，如需本地上传，请先从 <a href="/videoDrive#/?mode=list" target="_blank">课程资源 - 我的音视频</a> 中上传。`,
);

function getThumbImg(file: IMediaData) {
    let thumbImg = (file.thumb_info || '').split(',')[0];
    if (!thumbImg) {
        return '';
    }
    if (file.id) {
        return ImageImageHandle.preSignedUnbindUrl(
            ImageImageHandle.appendWidth(thumbImg, 100),
            file.id,
            ImageImageHandle.SOURCE_TYPE.TYPE_VIDEO,
        );
    }
    return '';
}

export const UmuVideoSelectorPC = (props: IUmuVideoSelectorProps) => {
    const { visible = true, status_str = 'in_use,transcoding,wait_transcoding', allowUpload = false, done } = props;
    const [order, setOrder] = useState({
        is_desc: 1,
        order_by: 'create_time',
    });
    const keywordsRef = useRef('');
    const [loading, setLoading] = useState(false);
    const [files, setFiles] = useState<IMediaData[]>([]);
    const [selectedFile, setSelectedFile] = useState<IMediaData>();
    const [pageInfo, setPageInfo] = useState(defaultPageInfo);

    useEffect(() => {
        doSearch(1);
    }, [order]);

    const doSearch = (page: number) => {
        setLoading(true);
        getResourceArr({
            page,
            page_rows: 10,
            status_str,
            ext_type: 'mp4',
            is_desc: order.is_desc,
            order_by: order.order_by,
            media_type: 'videoweike',
            search_keyword: keywordsRef.current,
        })
            .then((res) => {
                if (!res.error_code) {
                    setFiles(res.data.list);
                    setPageInfo(res.data.page_info);
                }
            })
            .finally(() => setLoading(false));
    };

    const onKeywordSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        const newValue = new FormData(e.target as HTMLFormElement).get('keyword');
        keywordsRef.current = newValue as string;
        doSearch(1);
        e.cancelable = false;
        e.preventDefault();
        return false;
    };

    const orderChange = (orderName: string) => {
        if (orderName === order.order_by) {
            setOrder({
                is_desc: order.is_desc === 0 ? 1 : 0,
                order_by: orderName,
            });
        } else {
            setOrder({
                is_desc: 0,
                order_by: orderName,
            });
        }
    };

    const selectFile = (file: IMediaData) => {
        if (file.status !== 'in_use') {
            setSelectedFile(void 0);
            return;
        }
        setSelectedFile(selectedFile?.id === file.id ? void 0 : file);
    };

    const renderTrs = () => {
        return files.map((file) => {
            const thumbImg = getThumbImg(file);
            return (
                <tr
                    key={file.id || file.url}
                    className={`file-item file-item-row file-item-type-mp4 ${
                        selectedFile && selectedFile.id == file.id ? 'active' : ''
                    } ${file.transcoding_url ? 'can-select' : ''}`}
                    onClick={() => selectFile(file)}
                >
                    <td>
                        <div className="title title-course">
                            <div className="file-name flex--start--center">
                                <div className="file-name-img flex--center">
                                    <If condition={!!thumbImg}>
                                        <img className="thumb-img" src={thumbImg} />
                                    </If>
                                </div>
                                <Tooltip overlayClassName="umu-video-selector__file-name-text" title={file.file_name}>
                                    <div className="file-name__name flex--1 umu-text-clamp clamp-1">
                                        {file.file_name}
                                    </div>
                                </Tooltip>
                            </div>
                        </div>
                    </td>
                    <td className="file-item-col date-time file-time">{file.create_time}</td>
                    <td className="file-item-col size">{formatFileSize(file.file_size)}</td>
                </tr>
            );
        });
    };

    const renderFooter = () => {
        return (
            <div className="umu-video-selector__footer flex--between--center">
                <div className="flex--1">
                    <div className="flex--start--center">
                        <div>{lang('共 {$0{0= 条,1= 条}}', pageInfo.list_total_num)}</div>
                        <If condition={pageInfo.total_page_num > 1}>
                            <div className="flex--1">
                                <Pagination
                                    showSizeChanger
                                    currentPage={pageInfo.current_page}
                                    maxPage={pageInfo.total_page_num}
                                    onPageChange={doSearch}
                                />
                            </div>
                        </If>
                    </div>
                </div>
                <div className="umu-video-selector__footer__operations flex--center">
                    <button
                        className="umu-video-selector__footer__operations__refresh flex--center"
                        onClick={() => doSearch(1)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <g clip-path="url(#clip0_324_8385)">
                                <path
                                    d="M0.327686 14.291C1.20041 18.9819 4.9095 22.8001 9.70951 23.6728C13.2004 24.3274 16.8004 23.4546 19.4186 21.1637C22.1459 18.8728 23.6731 15.6001 23.6731 12.1092H21.9277C21.9277 14.8364 20.8368 17.3455 18.8731 19.2001C16.9095 21.0546 14.2913 22.1455 11.564 22.0364C6.32769 21.9274 1.96405 17.5637 1.85496 12.3274C1.74587 9.60008 2.83678 6.9819 4.69132 5.01826C6.54587 3.05463 9.16405 1.96372 11.8913 1.96372C14.6186 1.96372 17.2368 3.05463 19.0913 5.01826L19.2004 5.12735H15.4913V6.87281H21.3822C21.6004 6.87281 21.8186 6.65463 21.8186 6.43644V0.218262H20.0731V3.60008L19.9641 3.49099C17.7822 1.41826 14.8368 0.218262 11.8913 0.218262C8.40041 0.218262 5.12769 1.74553 2.83678 4.47281C0.545868 7.20008 -0.326859 10.691 0.327686 14.291Z"
                                    fill="#222222"
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0_324_8385">
                                    <rect width="24" height="24" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </button>
                    <Button type="primary" disabled={!selectedFile} onClick={() => done(selectedFile!)}>
                        {lang('选择')}
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <Modal
            closable
            centered
            width={800}
            visible={visible}
            maskClosable={false}
            title={lang('我的音视频')}
            onCancel={props.cancel}
            wrapClassName="umu-video-selector-modal"
            cancelText=""
            okText=""
        >
            <div className="umu-video-selector">
                <If condition={!allowUpload}>
                    <div className="umu-video-selector__uplink" dangerouslySetInnerHTML={{ __html: uplinkText }} />
                </If>
                <form onSubmit={onKeywordSubmit} className="umu-video-selector__filter flex--start--center">
                    <input
                        type="text"
                        name="keyword"
                        className="umu-video-selector__filter__keyword"
                        placeholder={lang('文件名称')}
                    />
                    <button className="umu-video-selector__filter__btn flex--center" type="submit">
                        <Iconfont type="Search" />
                    </button>
                </form>
                <If condition={!loading && !!files.length}>
                    <div className="file-list-wrap">
                        <table>
                            <thead>
                                <tr className="file-item file-item-header">
                                    <th
                                        className={`file-item-col icon-activatable title ${
                                            order.is_desc ? 'active' : ''
                                        }`}
                                        onClick={() => orderChange('file_name')}
                                    >
                                        <span>{lang('文件名称')}</span>
                                        <If condition={order.order_by === 'file_name'}>
                                            <i className="icon icon-arrow arrow3 up"></i>
                                        </If>
                                    </th>
                                    <th
                                        className={`file-item-col icon-activatable date-time ${
                                            order.is_desc ? 'active' : ''
                                        }`}
                                        onClick={() => orderChange('create_time')}
                                    >
                                        {lang('上传时间')}
                                        <If condition={order.order_by === 'create_time'}>
                                            <i className="icon icon-arrow arrow3 up"></i>
                                        </If>
                                    </th>
                                    <th
                                        className={`file-item-col icon-activatable size ${
                                            order.is_desc ? 'active' : ''
                                        }`}
                                        onClick={() => orderChange('file_size')}
                                    >
                                        <span>{lang('文件大小')}</span>
                                        <If condition={order.order_by === 'file_size'}>
                                            <i className="icon icon-arrow arrow3 up"></i>
                                        </If>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>{renderTrs()}</tbody>
                        </table>
                    </div>
                </If>
                <Loading show={loading} />
                <If condition={!loading && !files.length}>
                    <ListBlankPC
                        type={keywordsRef.current ? 4 : 2}
                        title={keywordsRef.current ? lang('没有找到符合条件的音视频') : lang('暂无数据')}
                    />
                </If>
                {renderFooter()}
            </div>
        </Modal>
    );
};
